import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Pagination } from "@/components/ui/pagination";
import { formatNumber } from "@/lib/format";
import type { RegionCount } from "@/hooks/use-dashboard";
import { usePagination } from "@/hooks/use-pagination";

interface RegionListProps {
  title: string;
  description: string;
  rows: RegionCount[];
  emptyText: string;
  /** Nome dos itens no plural, para o rodapé de paginação. */
  itemLabel: string;
}

export function RegionList({ title, description, rows, emptyText, itemLabel }: RegionListProps) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  // A lista é inteira (todas as cidades, não só as primeiras), e a barra segue
  // proporcional à maior de todas, não à maior da página.
  const pagination = usePagination(rows, "", 8);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="py-4 text-small text-muted-foreground">{emptyText}</p>
        ) : (
          <ul className="space-y-2.5">
            {pagination.pageItems.map((row) => (
              <li key={row.key} className="space-y-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="min-w-0 truncate text-small font-semibold">{row.label}</span>
                  <span className="shrink-0 text-small tabular-nums text-muted-foreground">
                    {formatNumber(row.count)}
                  </span>
                </div>
                {/* A barra é a leitura rápida; o número é a exata. As duas
                    juntas evitam a pergunta "isso é muito ou pouco". */}
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary"
                    style={{ width: `${Math.round((row.count / max) * 100)}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
        <Pagination
          {...pagination.paginationProps}
          itemLabel={itemLabel}
          className="-mx-4 mt-3 sm:-mx-5"
        />
      </CardContent>
    </Card>
  );
}
